import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Toast from "../SnackBar/Toast";
import { hideSnackBar } from "../../store/common/snackBarSlice.js";
import { useTranslation } from "react-i18next";

const usePageHidden = () => {
  const [hidden, setHidden] = useState(
    typeof document !== "undefined" && document.hidden,
  );
  useEffect(() => {
    const onChange = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);
  return hidden;
};

const announcement = (toast) =>
  toast ? [toast.title, toast.text].filter(Boolean).join(". ") : "";

/**
 * The single place notifications render; mounted once in App.
 *
 * While the pointer or keyboard focus is on any toast, every timer pauses,
 * so reading one doesn't lose the others.
 *
 * Announcements go through two always-mounted, visually hidden live regions
 * (errors assertive, the rest polite) rather than the visible list: a region
 * must exist before its content changes to be announced reliably, and this
 * keeps each message announced once, without the close button's label.
 */
const CustomSnackbar = () => {
  // Named translate: "t" is a toast in this file.
  const { t: translate } = useTranslation("common");
  const dispatch = useDispatch();
  const items = useSelector((state) => state.snackBar.items);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const pageHidden = usePageHidden();
  const listRef = useRef(null);
  const returnFocusRef = useRef(null);
  const [announced, setAnnounced] = useState(null);
  const seenIdsRef = useRef(new Set());

  const dismiss = useCallback((id) => dispatch(hideSnackBar(id)), [dispatch]);

  // Native pointer events, not React's onMouseEnter/Leave: when the hovered
  // toast is removed (clicking its close button), Chrome fires a native
  // pointerleave on the list but no mouseout, which React's synthetic
  // mouseleave is built from, so the rest would stay paused for good.
  // Touch is ignored: a tap leaves a sticky hover that would pause everything
  // until the next tap elsewhere.
  useEffect(() => {
    const list = listRef.current;
    const enter = (e) => e.pointerType !== "touch" && setHovered(true);
    const leave = () => setHovered(false);
    list.addEventListener("pointerenter", enter);
    list.addEventListener("pointerleave", leave);
    return () => {
      list.removeEventListener("pointerenter", enter);
      list.removeEventListener("pointerleave", leave);
    };
  }, []);

  // A removed toast doesn't reliably fire blur, so re-derive focus from the
  // DOM whenever the list changes. If focus was on the toast that just went
  // away, hand it back to where it came from instead of dropping it on body.
  useEffect(() => {
    const list = listRef.current;
    const active = document.activeElement;
    const inside = list.contains(active);
    if (focused && !inside && (!active || active === document.body)) {
      const target = returnFocusRef.current;
      if (target?.isConnected) target.focus({ preventScroll: true });
    }
    setFocused(inside);
    if (!items.length) setHovered(false);
    // `focused` is read as of the render that removed the toast.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Announce a toast when it arrives, never again when a newer one is
  // dismissed and an older one becomes the newest.
  useEffect(() => {
    const fresh = items.filter((t) => !seenIdsRef.current.has(t.id));
    seenIdsRef.current = new Set(items.map((t) => t.id));
    if (fresh.length) setAnnounced(fresh[fresh.length - 1]);
    else if (!items.length) setAnnounced(null);
  }, [items]);

  const paused = hovered || focused || pageHidden;
  const announceError = announced?.severity === "error";

  return (
    <section
      className="bolt-toast__viewport"
      aria-label={translate("notifications.label")}
    >
      <ol
        ref={listRef}
        className="bolt-toast__list"
        onFocus={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            returnFocusRef.current = e.relatedTarget;
          }
          setFocused(true);
        }}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false);
        }}
      >
        {/* Newest first, nearest the corner. */}
        {[...items].reverse().map((toast) => (
          <Toast
            key={toast.id}
            toast={toast}
            paused={paused}
            onDismiss={dismiss}
          />
        ))}
      </ol>
      {/* Keyed by id so a repeated message is re-announced. */}
      <div
        className="bolt-toast__sr"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {announced && !announceError && (
          <span key={announced.id}>{announcement(announced)}</span>
        )}
      </div>
      <div
        className="bolt-toast__sr"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        {announceError && (
          <span key={announced.id}>{announcement(announced)}</span>
        )}
      </div>
    </section>
  );
};

export default CustomSnackbar;
